import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faEye, faEyeSlash, faCheckCircle, faTimesCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import AccountsUsersService from '../../../services/AccountsUsersService';
import { useRolePresets } from '../../../hooks/useRolePresets';
import { UserAccountSearch } from './UserAccountSearch';

const EMPTY = {
    user: '', name: '', ap_pat: '', ap_mat: '',
    password: '', email: '', phone: '', ext: '', area: '',
    type: 'editor', editorial: null,
};

// Mismas reglas que el backend (api/services/user/admin_create_user.php y
// admin_update_user.php). El backend sigue siendo la autoridad final; esto solo
// evita mandar una peticion que sabemos que va a rechazar y le dice al usuario
// que corregir antes de guardar.
const ONLY_LETTERS = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' .]+$/;
const PHONE = /^[0-9+\-() ]+$/;
const EXT = /^[0-9]+$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MIN_PASSWORD = 8;
const MAX = { user: 60, email: 150, name: 80, ap_pat: 80, ap_mat: 80, phone: 30, ext: 10, area: 100 };

// Campo del formulario -> campo del backend, para reintentar el foco cuando el
// error vuelve del servidor en vez de la validacion local.
const FIELD_BY_LABEL = [
    ['username', 'user'],
    ['correo', 'email'],
    ['email', 'email'],
    ['nombre', 'name'],
    ['apellido paterno', 'ap_pat'],
    ['apellido materno', 'ap_mat'],
    ['contraseña', 'password'],
    ['telefono', 'phone'],
    ['teléfono', 'phone'],
    ['ext', 'ext'],
    ['rol', 'type'],
    ['cuenta', 'editorial'],
];

function fieldFromBackendMessage(msg) {
    const lower = (msg || '').toLowerCase();
    const hit = FIELD_BY_LABEL.find(([label]) => lower.includes(label));
    return hit ? hit[1] : '';
}

export function UserForm({ initial, isNew, onSubmit, saving, onCancel }) {
    const { presets: roles, loading: rolesLoading, error: rolesError } = useRolePresets();
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [userCheck, setUserCheck] = useState({ checking: false, available: null, msg: '' });
    const [emailCheck, setEmailCheck] = useState({ checking: false, available: null, msg: '' });
    const [formError, setFormError] = useState('');

    useEffect(() => {
        const base = initial || {};
        setForm({
            user: base.user || '',
            name: base.name || '',
            ap_pat: base.ap_pat || '',
            ap_mat: base.ap_mat || '',
            password: '',
            email: base.email || '',
            phone: base.phone || '',
            ext: base.ext || '',
            area: base.area || '',
            type: base.type || 'editor',
            editorial: base.editorial
                ? { label: base.account_prefix ? `[${base.account_prefix}] ${base.editorial}` : base.editorial, value: base.editorial }
                : null,
        });
        setErrors({});
        setTouched({});
        setUserCheck({ checking: false, available: null, msg: '' });
        setEmailCheck({ checking: false, available: null, msg: '' });
        setFormError('');
    }, [initial, isNew]);

    const validate = (field, value) => {
        const v = typeof value === 'string' ? value.trim() : value;
        switch (field) {
            case 'user':
                if (!v) return 'El username es requerido';
                if (/\s/.test(v)) return 'El username no puede contener espacios';
                if (v.length > MAX.user) return `Máximo ${MAX.user} caracteres`;
                return '';
            case 'email':
                if (!v) return 'El email es requerido';
                if (v.length > MAX.email) return `Máximo ${MAX.email} caracteres`;
                return EMAIL.test(v) ? '' : 'Correo no válido';
            case 'name':
                if (!v) return 'El nombre es requerido';
                if (v.length > MAX.name) return `Máximo ${MAX.name} caracteres`;
                return ONLY_LETTERS.test(v) ? '' : 'Solo se permiten letras';
            case 'ap_pat':
                if (!v) return 'El apellido paterno es requerido';
                if (v.length > MAX.ap_pat) return `Máximo ${MAX.ap_pat} caracteres`;
                return ONLY_LETTERS.test(v) ? '' : 'Solo se permiten letras';
            case 'ap_mat':
                if (!v) return '';
                if (v.length > MAX.ap_mat) return `Máximo ${MAX.ap_mat} caracteres`;
                return ONLY_LETTERS.test(v) ? '' : 'Solo se permiten letras';
            case 'password':
                if (isNew && !v) return 'La contraseña es requerida';
                // El backend solo aplica el minimo a la contraseña nueva; al editar,
                // vacio significa "no cambiarla" y entonces no se valida nada.
                if (v && v.length < MIN_PASSWORD) return `Debe tener al menos ${MIN_PASSWORD} caracteres`;
                return '';
            case 'phone':
                if (!v) return '';
                if (v.length > MAX.phone) return `Máximo ${MAX.phone} caracteres`;
                return PHONE.test(v) ? '' : 'Solo se permiten números';
            case 'ext':
                if (!v) return '';
                if (v.length > MAX.ext) return `Máximo ${MAX.ext} caracteres`;
                return EXT.test(v) ? '' : 'Solo se permiten números';
            case 'area':
                return v && v.length > MAX.area ? `Máximo ${MAX.area} caracteres` : '';
            case 'editorial':
                return v ? '' : 'La cuenta es requerida';
            default:
                return '';
        }
    };

    const set = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        // Si el campo ya estaba marcado, se corrige el mensaje mientras se escribe;
        // asi el aviso desaparece en cuanto el dato es valido y no hay que esperar
        // a salir del input.
        if (touched[field]) {
            setErrors((prev) => ({ ...prev, [field]: validate(field, value) }));
        }
        if (field === 'user' || field === 'email') setFormError('');
    };

    const handleBlur = (field, value) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setErrors((prev) => ({ ...prev, [field]: validate(field, value) }));
    };

    const checkUserAvailability = async () => {
        const value = form.user.trim();
        if (!value || errors.user) {
            setUserCheck({ checking: false, available: null, msg: '' });
            return;
        }
        // When editing and the username is unchanged there is nothing to validate.
        if (!isNew && value === ((initial && initial.user) || '')) {
            setUserCheck({ checking: false, available: null, msg: '' });
            return;
        }
        setUserCheck({ checking: true, available: null, msg: '' });
        try {
            const res = await AccountsUsersService.validateUser({ user: value, id: isNew ? 0 : ((initial && initial.id) || 0) });
            setUserCheck({ checking: false, available: res.available, msg: res.available ? '' : 'Este username ya está en uso' });
        } catch (e) {
            // No se pudo comprobar (red, permisos...). No se bloquea el guardado:
            // el backend vuelve a comprobarlo y su respuesta es la definitiva.
            setUserCheck({ checking: false, available: null, msg: '' });
        }
    };

    const handleUserBlur = async () => {
        handleBlur('user', form.user);
        if (validate('user', form.user)) {
            setUserCheck({ checking: false, available: null, msg: '' });
            return;
        }
        await checkUserAvailability();
    };

    const checkEmailAvailability = async () => {
        const value = form.email.trim();
        if (!value || validate('email', value)) {
            setEmailCheck({ checking: false, available: null, msg: '' });
            return;
        }
        if (!isNew && value === ((initial && initial.email) || '')) {
            setEmailCheck({ checking: false, available: null, msg: '' });
            return;
        }
        setEmailCheck({ checking: true, available: null, msg: '' });
        try {
            const res = await AccountsUsersService.validateUser({ email: value, id: isNew ? 0 : ((initial && initial.id) || 0) });
            setEmailCheck({ checking: false, available: res.available, msg: res.available ? '' : 'Este correo ya está en uso' });
        } catch (e) {
            setEmailCheck({ checking: false, available: null, msg: '' });
        }
    };

    const handleEmailBlur = async () => {
        handleBlur('email', form.email);
        if (validate('email', form.email)) {
            setEmailCheck({ checking: false, available: null, msg: '' });
            return;
        }
        await checkEmailAvailability();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        const nextErrors = {};
        Object.keys(form).forEach((field) => {
            nextErrors[field] = validate(field, form[field]);
        });
        setErrors(nextErrors);
        setTouched((prev) => {
            const next = { ...prev };
            Object.keys(form).forEach((f) => { next[f] = true; });
            return next;
        });

        const firstInvalid = Object.keys(nextErrors).find((f) => nextErrors[f]);
        if (firstInvalid) {
            setFormError('Revisa los campos marcados en rojo.');
            focusField(firstInvalid);
            return;
        }
        if (userCheck.available === false) {
            setFormError('El username ya está en uso.');
            focusField('user');
            return;
        }
        if (emailCheck.available === false) {
            setFormError('El correo ya está en uso.');
            focusField('email');
            return;
        }

        const payload = {
            user: form.user.trim(),
            name: form.name.trim(),
            ap_pat: form.ap_pat.trim(),
            ap_mat: form.ap_mat.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            ext: form.ext.trim(),
            area: form.area.trim(),
            type: form.type,
            editorial: form.editorial ? form.editorial.value : '',
        };
        if (isNew || form.password) {
            payload.password = form.password;
        }

        // El backend devuelve mensajes concretos ("Rol no válido", "La cuenta X no
        // existe", "Ya existe un usuario..."). Antes se descartaban y el modal se
        // quedaba abierto sin explicar nada.
        const result = await onSubmit(payload);
        if (result && result.ok === false) {
            const msg = result.msg || 'No se pudo guardar el usuario.';
            setFormError(msg);
            const field = fieldFromBackendMessage(msg);
            if (field) {
                setErrors((prev) => ({ ...prev, [field]: msg }));
                focusField(field);
            }
        }
    };

    const focusField = (field) => {
        const el = document.querySelector(`[data-field="${field}"]`);
        if (el && typeof el.focus === 'function') {
            el.focus();
            if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    };

    const inputClass = (field) =>
        `form-control bg-dark text-white border-secondary py-2${errors[field] ? ' border-danger' : ''}`;

    const fieldError = (field) => (
        <div style={{ minHeight: '22px' }}>
            {errors[field] && <small className="text-danger d-block mt-1">{errors[field]}</small>}
        </div>
    );

    const renderUserInput = () => (
        <div className="input-group">
            <input
                type="text"
                data-field="user"
                className={inputClass('user')}
                value={form.user}
                autoComplete="off"
                maxLength={MAX.user}
                onChange={(e) => set('user', e.target.value)}
                onBlur={handleUserBlur}
            />
            <span className="input-group-text bg-dark border-secondary" style={{ minWidth: '40px', justifyContent: 'center' }}>
                {userCheck.checking
                    ? <FontAwesomeIcon icon={faSpinner} spin />
                    : userCheck.available === false
                        ? <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />
                        : userCheck.available === true
                            ? <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                            : null}
            </span>
        </div>
    );

    const renderPasswordInput = () => (
        <div className="input-group">
            <input
                type={showPassword ? 'text' : 'password'}
                data-field="password"
                className={inputClass('password')}
                value={form.password}
                autoComplete="new-password"
                onChange={(e) => set('password', e.target.value)}
                onBlur={() => handleBlur('password', form.password)}
                placeholder={isNew ? '' : 'Dejar vacío para no cambiar'}
            />
            <button
                type="button"
                className="input-group-text bg-dark border-secondary text-white-50"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
                title={showPassword ? 'Ocultar contraseña' : 'Ver contraseña'}
            >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
        </div>
    );

    return (
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
            {formError && (
                <div className="alert alert-danger d-flex align-items-center py-2 px-3 mb-3" role="alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <small>{formError}</small>
                </div>
            )}

            <div className="text-white-50 small fw-bold text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Datos requeridos</div>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label text-white-50">Username *</label>
                    {renderUserInput()}
                    <div style={{ minHeight: '22px' }}>
                        {errors.user
                            ? <small className="text-danger d-block mt-1">{errors.user}</small>
                            : userCheck.msg && <small className="text-danger d-block mt-1">{userCheck.msg}</small>}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Email *</label>
                    <div className="input-group">
                        <input
                            type="email"
                            data-field="email"
                            className={inputClass('email')}
                            value={form.email}
                            autoComplete="off"
                            maxLength={MAX.email}
                            onChange={(e) => set('email', e.target.value)}
                            onBlur={handleEmailBlur}
                        />
                        <span className="input-group-text bg-dark border-secondary" style={{ minWidth: '40px', justifyContent: 'center' }}>
                            {emailCheck.checking
                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                : emailCheck.available === false
                                    ? <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />
                                    : emailCheck.available === true
                                        ? <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                                        : null}
                        </span>
                    </div>
                    <div style={{ minHeight: '22px' }}>
                        {errors.email
                            ? <small className="text-danger d-block mt-1">{errors.email}</small>
                            : emailCheck.msg && <small className="text-danger d-block mt-1">{emailCheck.msg}</small>}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Nombre *</label>
                    <input
                        type="text"
                        data-field="name"
                        className={inputClass('name')}
                        value={form.name}
                        autoComplete="off"
                        maxLength={MAX.name}
                        onChange={(e) => set('name', e.target.value)}
                        onBlur={() => handleBlur('name', form.name)}
                    />
                    {fieldError('name')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Apellido paterno *</label>
                    <input
                        type="text"
                        data-field="ap_pat"
                        className={inputClass('ap_pat')}
                        value={form.ap_pat}
                        autoComplete="off"
                        maxLength={MAX.ap_pat}
                        onChange={(e) => set('ap_pat', e.target.value)}
                        onBlur={() => handleBlur('ap_pat', form.ap_pat)}
                    />
                    {fieldError('ap_pat')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Apellido materno</label>
                    <input
                        type="text"
                        data-field="ap_mat"
                        className={inputClass('ap_mat')}
                        value={form.ap_mat}
                        autoComplete="off"
                        maxLength={MAX.ap_mat}
                        onChange={(e) => set('ap_mat', e.target.value)}
                        onBlur={() => handleBlur('ap_mat', form.ap_mat)}
                    />
                    {fieldError('ap_mat')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Rol *</label>
                    <select
                        data-field="type"
                        className={`form-select bg-dark text-white border-secondary py-2${errors.type ? ' border-danger' : ''}`}
                        value={form.type}
                        disabled={rolesLoading}
                        onChange={(e) => set('type', e.target.value)}
                    >
                        {rolesLoading ? (
                            <option value={form.type || ''}>Cargando roles...</option>
                        ) : (
                            <>
                                {/* Un usuario puede conservar un rol que ya no exista en
                                    role_presets; sin esta opcion el select quedaria vacio. */}
                                {form.type && !roles.some((r) => r.code === form.type) && (
                                    <option value={form.type}>{form.type}</option>
                                )}
                                {roles.map((r) => (
                                    <option key={r.code} value={r.code}>{r.name}</option>
                                ))}
                            </>
                        )}
                    </select>
                    {rolesError && <small className="text-danger d-block mt-1">{rolesError}</small>}
                    {fieldError('type')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Contraseña {isNew ? '*' : ''}</label>
                    {renderPasswordInput()}
                    <div style={{ minHeight: '22px' }}>
                        {errors.password
                            ? <small className="text-danger d-block mt-1">{errors.password}</small>
                            : <small className="text-white-50 d-block mt-1">
                                {isNew ? `Mínimo ${MIN_PASSWORD} caracteres.` : `Mínimo ${MIN_PASSWORD} caracteres si se cambia.`}
                            </small>}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Cuenta *</label>
                    <UserAccountSearch
                        value={form.editorial}
                        onChange={(opt) => {
                            set('editorial', opt);
                            if (touched.editorial) setErrors((prev) => ({ ...prev, editorial: opt ? '' : 'La cuenta es requerida' }));
                        }}
                        onBlur={() => handleBlur('editorial', form.editorial ? form.editorial.value : '')}
                        placeholder="Busca una cuenta..."
                    />
                    {fieldError('editorial')}
                </div>
            </div>

            <div className="text-white-50 small fw-bold text-uppercase mt-4 mb-2" style={{ letterSpacing: '0.05em' }}>Datos opcionales</div>
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label text-white-50">Teléfono</label>
                    <input
                        type="text"
                        data-field="phone"
                        className={inputClass('phone')}
                        value={form.phone}
                        autoComplete="off"
                        maxLength={MAX.phone}
                        onChange={(e) => set('phone', e.target.value)}
                        onBlur={() => handleBlur('phone', form.phone)}
                        placeholder="Solo números"
                    />
                    {fieldError('phone')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Ext</label>
                    <input
                        type="text"
                        data-field="ext"
                        className={inputClass('ext')}
                        value={form.ext}
                        autoComplete="off"
                        maxLength={MAX.ext}
                        onChange={(e) => set('ext', e.target.value)}
                        onBlur={() => handleBlur('ext', form.ext)}
                        placeholder="Solo números"
                    />
                    {fieldError('ext')}
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Área</label>
                    <input
                        type="text"
                        data-field="area"
                        className={inputClass('area')}
                        value={form.area}
                        autoComplete="off"
                        maxLength={MAX.area}
                        onChange={(e) => set('area', e.target.value)}
                        onBlur={() => handleBlur('area', form.area)}
                    />
                    {fieldError('area')}
                </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
                <button type="button" className="btn btn-outline-secondary px-4 rounded-pill" onClick={onCancel} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-success px-4 rounded-pill" disabled={saving}>
                    {saving && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                    {isNew ? 'Crear usuario' : 'Guardar cambios'}
                </button>
            </div>
        </form>
    );
}

export default UserForm;
